import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import PortalWizard from './components/PortalWizard';
import InactivityWarningModal from './components/InactivityWarningModal';
import useInactivityTimeout from './hooks/useInactivityTimeout';
import Login from './pages/Login';
import MyFeed from './pages/MyFeed';
import Dashboard from './pages/Dashboard';
import VehicleList from './pages/VehicleList';
import DriverList from './pages/DriverList';
import TrailerList from './pages/TrailerList';
import ClaimsDashboard from './pages/ClaimsDashboard';
import ClaimWorkspace from './pages/ClaimWorkspace';
import ClientList from './pages/ClientList';
import SiteList from './pages/SiteList';
import UserList from './pages/UserList';
import WorkstreamList from './pages/WorkstreamList';
import CoverNotices from './pages/CoverNotices';
import InsightsReporting from './pages/InsightsReporting';
import ClientInsights from './pages/ClientInsights';
import AdminTools from './pages/AdminTools';
import ConsentRecords from './pages/ConsentRecords';
import ClientDetails from './pages/ClientDetails';
import SecurityCompliance from './pages/SecurityCompliance';
import Academy from './pages/Academy';
import AcademyWelcomeModal from './components/academy/AcademyWelcomeModal';
import AcademyTrainingGate from './components/academy/AcademyTrainingGate';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Layout from './components/Layout';
import { academyAPI } from './services/api';
import './App.css';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { token, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  return token ? <>{children}</> : <Navigate to="/login" />;
}

/**
 * Like PrivateRoute but also enforces mandatory Academy completion.
 * Accepts trainingCheckDone so it waits for the backend sync before
 * showing the gate — prevents flickering for users who have already completed.
 */
function TrainingGatedRoute({
  children,
  trainingCheckDone,
}: {
  children: React.ReactNode;
  trainingCheckDone: boolean;
}) {
  const { token, loading, user } = useAuth();
  if (loading || !trainingCheckDone) return <div className="loading">Loading...</div>;
  if (!token) return <Navigate to="/login" />;

  const isTrainingExempt = user?.trainingRequired === false ||
    (user?.trainingRequired === undefined && (user?.role === 'SuperAdmin' || user?.role === 'FleetManagerAdmin'));
  const academyDone  = user?.id ? !!localStorage.getItem(`academyDone-${user.id}`) : false;
  if (!isTrainingExempt && !academyDone) {
    return <AcademyTrainingGate />;
  }
  return <>{children}</>;
}

function SecurityRoute({ children }: { children: React.ReactNode }) {
  const { token, loading, user } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!token) return <Navigate to="/login" />;
  if (user?.role !== 'SuperAdmin' && user?.role !== 'SuperUser') return <Navigate to="/dashboard" />;
  return <>{children}</>;
}

function AppContent() {
  const { token, loading, logout, user } = useAuth();
  const location = useLocation();
  const { showWarning, minutesRemaining, resetTimer } = useInactivityTimeout();
  const [showAcademyModal, setShowAcademyModal] = useState(false);
  // Tracks whether the backend academy-completion sync has finished.
  // Until it resolves, TrainingGatedRoute shows a neutral loading screen
  // rather than the gate — prevents flickering for already-trained users.
  const [trainingCheckDone, setTrainingCheckDone] = useState(false);

  // On login, sync academy completion from the backend so users who completed
  // training on another device (or whose localStorage was cleared) are not blocked.
  useEffect(() => {
    if (!token || !user) { setTrainingCheckDone(true); return; }
    if (user.trainingRequired === false ||
        (user.trainingRequired === undefined && (user.role === 'SuperAdmin' || user.role === 'FleetManagerAdmin'))) {
      setTrainingCheckDone(true);
      return;
    }
    if (localStorage.getItem(`academyDone-${user.id}`)) { setTrainingCheckDone(true); return; }
    academyAPI.getProgress()
      .then(res => {
        const data = res.data;
        // getProgress returns { allPassed, modules, ... } — not a flat array
        const allPassed = data?.allPassed === true;
        const modules: any[] = Array.isArray(data?.modules) ? data.modules : [];
        const passedCount = modules.filter((m: any) => m.passed).length;
        if (allPassed || passedCount >= 5) {
          localStorage.setItem(`academyDone-${user.id}`, 'true');
        }
      })
      .catch(() => {})
      .finally(() => setTrainingCheckDone(true));
  }, [token, user?.id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show Academy welcome modal only for users who still need to train
  // (gate now enforces it, but modal acts as a friendly prompt on first login).
  useEffect(() => {
    if (!token || !user || user.trainingRequired === false ||
        (user.trainingRequired === undefined && (user.role === 'SuperAdmin' || user.role === 'FleetManagerAdmin'))) return;
    const userId = user.id;
    const iv = setInterval(() => {
      const gdprAccepted = sessionStorage.getItem('gdprConsent') === 'accepted';
      const wizardPending = sessionStorage.getItem('fleetWizardPending');
      const modalShown   = sessionStorage.getItem('academyModalShown');
      const academyDone  = localStorage.getItem(`academyDone-${userId}`);
      if (gdprAccepted && wizardPending === null && !modalShown && !academyDone) {
        sessionStorage.setItem('academyModalShown', '1');
        setShowAcademyModal(true);
        clearInterval(iv);
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [token, user?.id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) return;
    const handleSessionTimeout = () => logout();
    window.addEventListener('session-inactivity-timeout', handleSessionTimeout);
    return () => {
      window.removeEventListener('session-inactivity-timeout', handleSessionTimeout);
    };
  }, [token, logout]);

  if (loading) return <div className="loading">Loading...</div>;
  const isPublicRoute = ['/login', '/forgot-password', '/reset-password'].includes(location.pathname);
  const canShowPortalWizard = Boolean(token && user && !isPublicRoute);

  return (
    <>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Navigate to="/my-feed" />} />
      {/* My Feed and Academy bypass the training gate — accessible during training */}
      <Route path="/my-feed" element={<PrivateRoute><Layout><MyFeed /></Layout></PrivateRoute>} />
      <Route path="/academy" element={<PrivateRoute><Layout><Academy /></Layout></PrivateRoute>} />

      {/* All other routes require completed training (TrainingGatedRoute) */}
      <Route path="/dashboard" element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><Dashboard /></Layout></TrainingGatedRoute>} />
      <Route path="/vehicles"  element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><VehicleList /></Layout></TrainingGatedRoute>} />
      <Route path="/trailers"  element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><TrailerList /></Layout></TrainingGatedRoute>} />
      <Route path="/claims"    element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><ClaimsDashboard /></Layout></TrainingGatedRoute>} />
      <Route path="/claims/:id" element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><ClaimWorkspace /></Layout></TrainingGatedRoute>} />
      <Route path="/drivers"   element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><DriverList /></Layout></TrainingGatedRoute>} />
      <Route path="/clients"   element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><ClientList /></Layout></TrainingGatedRoute>} />
      <Route path="/clients/:id" element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><ClientDetails /></Layout></TrainingGatedRoute>} />
      <Route path="/sites"     element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><SiteList /></Layout></TrainingGatedRoute>} />
      <Route path="/users"     element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><UserList /></Layout></TrainingGatedRoute>} />
      <Route path="/workstream" element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><WorkstreamList /></Layout></TrainingGatedRoute>} />
      <Route path="/messages"  element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><CoverNotices /></Layout></TrainingGatedRoute>} />
      <Route path="/insights"  element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><InsightsReporting /></Layout></TrainingGatedRoute>} />
      <Route path="/admin-tools" element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><AdminTools /></Layout></TrainingGatedRoute>} />
      <Route path="/consent-records" element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><ConsentRecords /></Layout></TrainingGatedRoute>} />
      <Route path="/reports"       element={<TrainingGatedRoute trainingCheckDone={trainingCheckDone}><Layout><ClientInsights /></Layout></TrainingGatedRoute>} />
      <Route path="/security-compliance" element={<SecurityRoute><Layout><SecurityCompliance /></Layout></SecurityRoute>} />
      </Routes>
      {canShowPortalWizard && <PortalWizard />}
      {token && showAcademyModal && user?.trainingRequired !== false &&
        !(user?.trainingRequired === undefined && (user?.role === 'SuperAdmin' || user?.role === 'FleetManagerAdmin')) && (
        <AcademyWelcomeModal onDismiss={() => setShowAcademyModal(false)} />
      )}
      {token && (
        <InactivityWarningModal
          showWarning={showWarning}
          minutesRemaining={minutesRemaining}
          resetTimer={resetTimer}
          onLogout={logout}
        />
      )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
